import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, GraduationCap, CalendarCheck2, Wallet } from "lucide-react";
import { getStudentAnalytics } from "../../api/student/studentAnalyticsApi";
import { getStaffAnalytics } from "../../api/staff/staffAnalyticsApi";
import { getStudentAttendanceAnalytics } from "../../api/student/studentAttendanceAnalyticsApi";
import { getPayrollAnalytics } from "../../api/payroll/payrollAnalyticsApi";
import { Skeleton, Card } from "../ui";

const formatINR = (n) => {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

function StatCard({ icon: Icon, label, value, sub, tone = "default", onClick }) {
  const toneClasses = {
    default: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500/20",
    sky: "bg-sky-50 text-sky-700 ring-1 ring-sky-500/20",
    success: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/20",
    warning: "bg-amber-50 text-amber-700 ring-1 ring-amber-500/20",
    danger: "bg-rose-50 text-rose-700 ring-1 ring-rose-500/20",
  };

  return (
    <Card
      onClick={onClick}
      className="group flex items-center gap-3.5 border-slate-200/80 bg-white dark:bg-neutral-900 p-4 text-left shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md cursor-pointer active:scale-[0.99] rounded-2xl"
    >
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-105 shadow-2xs ${toneClasses[tone]}`}>
        <Icon className="h-6 w-6" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-2xl font-black tracking-tight text-slate-900 dark:text-neutral-100 leading-tight tabular-nums">{value}</p>
        <p className="mt-0.5 text-xs font-bold text-slate-600 dark:text-neutral-300 uppercase tracking-wider">{label}</p>
        {sub && <p className="mt-0.5 text-[11px] font-semibold text-slate-400 dark:text-neutral-500">{sub}</p>}
      </div>
    </Card>
  );
}

export default function QuickStatsWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: null,
    activeStudents: null,
    totalStaff: null,
    activeStaff: null,
    onLeave: null,
    todayRate: null,
    netPayroll: null,
    pendingPayroll: null,
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [studentRes, staffRes, attendanceRes, payrollRes] = await Promise.allSettled([
          getStudentAnalytics({ period: "year" }),
          getStaffAnalytics("year"),
          getStudentAttendanceAnalytics({ period: "month" }),
          getPayrollAnalytics({ year: String(new Date().getFullYear()) }),
        ]);

        if (cancelled) return;

        const student = studentRes.status === "fulfilled" ? studentRes.value?.kpis || {} : {};
        const staff = staffRes.status === "fulfilled" ? staffRes.value?.kpis || {} : {};
        const attendance = attendanceRes.status === "fulfilled" ? attendanceRes.value?.kpis || {} : {};
        const payroll = payrollRes.status === "fulfilled" ? payrollRes.value?.kpis || {} : {};

        setStats({
          totalStudents: student.totalStudents,
          activeStudents: student.activeStudents,
          totalStaff: staff.totalStaff,
          activeStaff: staff.activeStaff,
          onLeave: staff.onLeave,
          todayRate: attendance.todayRate,
          netPayroll: payroll.totalNet,
          pendingPayroll: payroll.pendingAmount,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        <Skeleton height={88} width="100%" className="rounded-2xl" />
        <Skeleton height={88} width="100%" className="rounded-2xl" />
        <Skeleton height={88} width="100%" className="rounded-2xl" />
        <Skeleton height={88} width="100%" className="rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
      <StatCard
        icon={GraduationCap}
        label="Students"
        tone="sky"
        value={stats.totalStudents ?? "—"}
        sub={stats.activeStudents != null ? `${stats.activeStudents} active` : undefined}
        onClick={() => navigate("/students")}
      />
      <StatCard
        icon={Users}
        label="Staff"
        value={stats.totalStaff ?? "—"}
        sub={stats.onLeave ? `${stats.onLeave} on leave` : undefined}
        tone={stats.onLeave > 0 ? "warning" : "default"}
        onClick={() => navigate("/staff")}
      />
      <StatCard
        icon={CalendarCheck2}
        label="Attendance Today"
        value={stats.todayRate != null ? `${stats.todayRate}%` : "—"}
        tone={
          stats.todayRate >= 85 ? "success" : stats.todayRate >= 70 ? "warning" : "danger"
        }
        onClick={() => navigate("/staff-attendance")}
      />
      <StatCard
        icon={Wallet}
        label="Payroll (Net, this month)"
        value={formatINR(stats.netPayroll)}
        sub={stats.pendingPayroll ? `${formatINR(stats.pendingPayroll)} pending` : undefined}
        tone={stats.pendingPayroll > 0 ? "warning" : "default"}
        onClick={() => navigate("/payroll-runs")}
      />
    </div>
  );
}